// A game's chat (SPEC §5.6, docs/plans/2026-10-27-game-chat.md): its messages, how far the viewer
// has seen them, and whether the chat window (phone) or panel (desktop) is shown. The game page
// owns one and decides when it opens; GameChat shows it.
import api from '@/api/api';
import { errorText } from '@/texts';

// Refusals that sending again can't change: no automatic retry.
const FINAL = ['chat-empty', 'chat-too-long', 'not-found', 'not-signed-in', 'forbidden', 'bad-request'];
// The automatic retry waits a little over the one-message-a-second limit.
const RETRY_AFTER = 1500;

export default class Chat {
  constructor(gameId, me) {
    this.gameId = gameId;
    this.me = me;
    // The stored messages, by number, then those of this page still being sent (n: null).
    this.messages = [];
    this.pending = [];
    // The viewer's read mark (messages up to this number have been seen); null until loaded.
    this.readUpTo = null;
    // The new-message line is drawn before the first message after this number (set on opening).
    this.dividerAfter = null;
    this.shown = false;
    this.loading = true;
    this.loadFailed = false;
    this.nextLocal = 1;
  }

  get lastN() {
    return this.messages.length ? this.messages[this.messages.length - 1].n : 0;
  }

  // Others' messages the viewer hasn't seen.
  get unread() {
    if (this.readUpTo === null) return [];
    return this.messages.filter(m => m.n > this.readUpTo && m.user.id !== this.me);
  }

  url(path = '') {
    return `games/${this.gameId}/${path}`;
  }

  // Reads the messages and the read mark. A spectator opening the chat of a game for the first
  // time has nothing new; for a player, everything others wrote is new until seen.
  async load(spectator) {
    this.loading = true;
    this.loadFailed = false;
    try {
      const list = await api.call('GET', this.url('chat-messages'));
      let read = null;
      try {
        read = (await api.call('GET', this.url(`chat-reads/${this.me}`))).upTo;
      } catch (e) {
        if (e.msg !== 'not-found') throw e;
      }
      this.merge(list);
      if (read === null && spectator) {
        this.readUpTo = 0;
        this.setRead(this.lastN);
      } else {
        this.readUpTo = read || 0;
      }
    } catch (e) {
      this.loadFailed = true;
    } finally {
      this.loading = false;
    }
  }

  // Adds messages (from a reply or the live connection) in order of their numbers; returns those
  // that weren't known yet. A message this page sent leaves the pending list when it arrives.
  merge(list) {
    const added = [];
    for (const m of list) {
      if (this.messages.some(x => x.n === m.n)) continue;
      const i = this.messages.findIndex(x => x.n > m.n);
      if (i < 0) this.messages.push(m); else this.messages.splice(i, 0, m);
      this.pending = this.pending.filter(p => p.n !== m.n);
      added.push(m);
    }
    return added;
  }

  // Asks for the messages after the latest one known; returns those that are new.
  async fetchNew() {
    if (this.loading) return [];
    if (this.loadFailed) {
      await this.load(false);
      return [];
    }
    try {
      return this.merge(await api.call('GET', this.url(`chat-messages?after=${this.lastN}`)));
    } catch (e) {
      return [];
    }
  }

  async setRead(upTo) {
    if (this.readUpTo !== null && upTo <= this.readUpTo) return;
    this.readUpTo = upTo;
    try {
      await api.call('PUT', this.url(`chat-reads/${this.me}`), { upTo });
    } catch (e) {
      // Not saved: the messages count as new again on the next visit.
    }
  }

  // Everything shown has been seen (the window or panel is open with the page in front).
  markRead() {
    if (this.shown && !document.hidden && this.readUpTo !== null) this.setRead(this.lastN);
  }

  open() {
    if (this.shown) return;
    this.dividerAfter = this.unread.length ? this.readUpTo : null;
    this.shown = true;
    this.markRead();
  }

  close() {
    this.shown = false;
    this.dividerAfter = null;
  }

  toggle() {
    if (this.shown) this.close(); else this.open();
  }

  // Sends a message; it shows at once, faded until the server has it. A send that fails is tried
  // once more by itself after a moment (SPEC §5.6), unless the text itself is refused; only then
  // is it shown as failed, for the viewer to send again.
  async send(text, retry = null) {
    const p = retry || { local: this.nextLocal++, text, n: null, failed: null };
    if (retry) p.failed = null; else this.pending.push(p);
    for (let attempt = 0; ; attempt++) {
      try {
        const { n } = await api.call('POST', this.url('chat-messages'), { text: p.text });
        p.n = n;
        if (this.messages.some(m => m.n === n)) this.pending = this.pending.filter(x => x !== p);
        else await this.fetchNew();
        this.markRead();
        return;
      } catch (e) {
        const code = e.msg || String(e);
        if (attempt === 0 && !FINAL.includes(code)) {
          await new Promise(resolve => setTimeout(resolve, RETRY_AFTER));
          continue;
        }
        p.failed = errorText(code);
        return;
      }
    }
  }
}
